<template>
    <GuestLayout class="shared-trip-container">
      <div v-if="isLoading" class="loading-container">
        <p class="mt-4 text-gray-600">{{ $t('shared_trip.loading') }}</p>
      </div>

    <div v-else-if="error" class="error-container">
      <BaseIcon icon="exclamation-triangle" class="text-red-500 text-4xl mb-4" />
      <h2 class="text-xl font-semibold text-gray-800 mb-2">{{ $t('shared_trip.error_loading') }}</h2>
      <p class="text-gray-600">{{ error }}</p>
    </div>

    <!-- OTP Verification Screen -->
    <div v-else-if="otpRequired" class="otp-container">
      <div class="otp-card">
        <div class="otp-icon-wrap">
          <BaseIcon icon="lock" class="otp-lock-icon" />
        </div>
        <h2 class="otp-title">{{ $t('shared_trip.otp_title') }}</h2>
        <p class="otp-desc">{{ $t('shared_trip.otp_description') }}</p>
        <div class="otp-input-group">
          <input
            v-model="otpInput"
            type="text"
            maxlength="6"
            inputmode="numeric"
            pattern="[0-9]*"
            class="otp-input"
            :class="{ 'otp-input-error': otpError }"
            :placeholder="$t('shared_trip.otp_placeholder')"
            :disabled="otpLoading"
            @keyup.enter="verifyOtp"
            @input="otpError = ''"
            autofocus
          />
          <div class="otp-error-container">
            <p v-if="otpError" class="otp-error-msg">
              <BaseIcon icon="exclamation-circle" class="mr-1" />{{ otpError }}
            </p>
          </div>
        </div>
        <button
          @click="verifyOtp"
          :disabled="otpLoading || !otpInput.trim()"
          class="otp-verify-btn"
        >
          <BaseIcon v-if="otpLoading" icon="spinner" class="fa-spin mr-2" />
          <BaseIcon v-else icon="check" class="mr-2" />
          {{ otpLoading ? $t('shared_trip.otp_verifying') : $t('shared_trip.otp_verify') }}
        </button>
        <p class="otp-hint">{{ $t('shared_trip.otp_hint') }}</p>
      </div>
    </div>

    <div v-else-if="trip" class="trip-view">
      <!-- Map Section -->
      <div class="map-section">
        <GmapMap :center="mapCenter" :zoom="mapZoom" map-type-id="roadmap" class="map-container" ref="map" @click="closeTrackedPointInfo">
          <GmapInfoWindow :options="infoOptions" :position="infoWindowPos" :opened="infoWinOpen" @closeclick="infoWinOpen = false"></GmapInfoWindow>
            <DirectionsRenderer travelMode="DRIVING" stopover="true" :origin="startLocation" :destination="endLocation" :waypoints="waypoints" :options="{ polylineOptions: { strokeColor: '#2563EB', strokeOpacity: 0.9, strokeWeight: 6, zIndex: 100 } }" />
            <GmapPolyline :path="definedRoute" :options="{ strokeColor: '#2563EB', strokeOpacity: 0.9, strokeWeight: 6, zIndex: 100 }" />
            
            <!-- Tracked route with segment markers for hover -->
            <!-- <GmapPolyline :path="trackedRoute" :options="{ strokeColor: '#DC2626', strokeOpacity: 0.95, strokeWeight: 6, zIndex: 101 }" /> -->
            
            <!-- Segment polylines for hover and click detection -->
            <GmapPolyline 
             v-for="(point, index) in trackedRoute" 
              :key="'segment-polyline-' + index" 
              :path="trackedRoute.slice(index, index + 2)"
              :options="{ 
                strokeColor: '#DC2626',
                strokeOpacity: 0.95,
                strokeWeight: 6,
                zIndex: 101,
              }"
              @mouseover="showTrackedPointInfo(point, index)"
              @mouseout="closeTrackedPointInfoUnlessPinned"
              @click="pinTrackedPointInfo(point, index)"
            ></GmapPolyline>
            
            <!-- Waiting circles (yellow) -->
            <GmapCircle v-for="(pin, index) in waitingCircles" :key="'waiting-' + index" :center="pin" :options="{ fillColor: '#F59E0B', fillOpacity: 0.4, radius: 200, strokeColor: '#F59E0B', strokeOpacity: 0.8, strokeWeight: 2, zIndex: 50 }" />

            <!-- Real-time car marker -->
            <GmapMarker v-if="carPosition" :position="carPosition" :options="{ icon: { url: require('@/assets/icon/car.png'), scaledSize: { width: 40, height: 40 }, anchor: { x: 20, y: 20 } }, zIndex: 1000 }" ref="carMarker" @click="showCarInfo"></GmapMarker>
        </GmapMap>
      </div>

      <!-- Details Section -->
      <div class="details-section">
        <div class="details-header">
          <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
            <BaseIcon icon="route" class="text-blue-600 text-2xl flex-shrink-0" />
            <h1 class="text-xl sm:text-2xl font-bold text-gray-800 flex-1 min-w-0 truncate">{{ trip.route_name || $t('shared_trip.trip_details') }}</h1>

            <!-- Map links -->
            <div class="flex items-center gap-2 flex-shrink-0">
              <button v-if="!checkIOS()" @click="openMapLink('google')" title="Open in Google Maps" class="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-blue-600 rounded-lg shadow-sm hover:shadow-md hover:bg-gray-50 hover:border-blue-300 transition-all duration-300 text-xs font-semibold text-gray-700">
                <img :src="require('../../assets/icon/google-maps.png')" class="w-4 h-4 flex-shrink-0" />
                <span class="hidden sm:inline">{{ $t('shared_trip.open_in_google_maps') }}</span>
              </button>
              <button v-if="checkIOS()" @click="openMapLink('apple')" title="Open in Apple Maps" class="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-blue-600 rounded-lg shadow-sm hover:shadow-md hover:bg-gray-50 hover:border-blue-300 transition-all duration-300 text-xs font-semibold text-gray-700">
                <img :src="require('../../assets/icon/apple-map-pin.png')" class="w-4 h-4 flex-shrink-0" />
                <span class="hidden sm:inline">{{ $t('shared_trip.open_in_apple_maps') }}</span>
              </button>
            </div>
          </div>
        </div>

        <div class="details-content">

          <!-- Start Trip button (hidden once started) -->
          <div class="trip-start-wrap" v-if="trip.trip_state && !trip.trip_state.tripStarted">
            <button
              @click="startTrip"
              :disabled="!canStartTrip || isTripExpired || isStartingTrip"
              class="start-trip-btn"
              :class="canStartTrip && !isTripExpired ? 'start-trip-btn-active' : 'start-trip-btn-disabled'"
            >
              <span v-if="isStartingTrip" class="btn-spinner"></span>
              <BaseIcon v-else icon="play" class="mr-2" />
              {{ isStartingTrip ? $t('shared_trip.starting') : $t('shared_trip.start_trip') }}
            </button>
            <p class="trip-start-note">
              <template v-if="isTripExpired">{{ $t('shared_trip.trip_start_expired_note') }}</template>
              <template v-else-if="!canStartTrip">{{ $t('shared_trip.trip_start_available_from', { time: departureWindowStart }) }}</template>
            </p>
          </div>

          <!-- Alarm/Device Details Card -->
          <div class="info-card" v-if="alarm">
            <h3 class="info-card-title">
              <BaseIcon icon="mobile-alt" class="mr-2" />
              {{ $t('shared_trip.device_information') }}
            </h3>
            <div class="info-grid">
              <div class="info-item" v-if="alarm.unit_name">
                <span class="info-label">{{ $t('shared_trip.unit_name') }}</span>
                <span class="info-value">{{ alarm.unit_name }}</span>
              </div>
              <div class="info-item" v-if="alarm.type">
                <span class="info-label">{{ $t('shared_trip.device_type') }}</span>
                <span class="info-value">{{ alarm.type }}</span>
              </div>
              <div class="info-item" v-if="alarm.imei_number">
                <span class="info-label">{{ $t('shared_trip.imei_number') }}</span>
                <span class="info-value">{{ alarm.imei_number }}</span>
              </div>
              <div class="info-item" v-if="alarm.phone && alarm.phone !== '-'">
                <span class="info-label">{{ $t('shared_trip.phone') }}</span>
                <span class="info-value">{{ alarm.phone }}</span>
              </div>
            </div>
          </div>

          <!-- Trip Info Card -->
          <div class="info-card">
            <h3 class="info-card-title">{{ $t('shared_trip.general_information') }}</h3>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">{{ $t('shared_trip.departure_time') }}</span>
                <span class="info-value">{{ formatDate(trip.departure_time) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">{{ $t('shared_trip.total_distance') }}</span>
                <span class="info-value">{{ (trip.total_distance / 1000).toFixed(2) }} km</span>
              </div>
              <div class="info-item" v-if="trip.total_duration">
                <span class="info-label">{{ $t('shared_trip.total_duration') }}</span>
                <span class="info-value">{{ formatDuration(trip.total_duration) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">{{ $t('shared_trip.trip_status') }}</span>
                <span class="info-value">
                  <span v-if="trip.trip_state && trip.trip_state.tripStarted == false && trip.trip_state.tripEnded == false" class="status-badge status-not-started">
                    {{ $t('shared_trip.trip_not_started') }}
                  </span>
                  <span v-else-if="trip.trip_state && trip.trip_state.tripStarted == true && trip.trip_state.tripEnded == false" class="status-badge status-ongoing">
                    {{ $t('shared_trip.trip_ongoing') }}
                  </span>
                  <span v-else-if="trip.trip_state && trip.trip_state.tripStarted == true && trip.trip_state.tripEnded == true" class="status-badge status-completed">
                    {{ $t('shared_trip.trip_completed') }}
                  </span>
                </span>
              </div>
            </div>
          </div>

          <!-- Route Card -->
          <div class="info-card">
            <h3 class="info-card-title">{{ $t('shared_trip.route_details') }}</h3>
            <RoutePreviewTimeline :waypoints="trip.waypoints" />
          </div>
        </div>
      </div>
    </div>
  </GuestLayout>
</template>

<script>
import { gmapApi } from "vue2-google-maps";
import moment from 'moment-timezone';
import GuestLayout from '@/components/layouts/guest_layout.vue';
import DirectionsRenderer from "@/components/map/DirectionsRenderer";
import RoutePreviewTimeline from "@/components/secure_route/route_preview_timeline";
import BaseIcon from '@/components/base/_base_icon.vue';

export default {
  name: 'SharedTrip',
  
  components: {
      GuestLayout,
    DirectionsRenderer,
      RoutePreviewTimeline,
      BaseIcon
  },

  data() {
    return {
      token: null,
      trip: null,
      alarm: null,
      gps: null,
      isLoading: true,
      error: null,
      mapCenter: { lat: 59.33, lng: 18.07 }, // Default to Stockholm
      mapZoom: 10,
      destinations: [],
      waitingCircles: [],
      waypoints: [],
      mapMarkers: [],
      infoWindowPos: null,
      infoWinOpen: false,
      infoOptions: { content: "", icon: "", pixelOffset: { width: 0, height: -35 } },
      selectedUnits: null,
      routePolyline: [],
      startLocation: null,
      endLocation: null,
      definedRoute: [],
      trackedRoute: [],
      carPosition: null, 
      carMarker: null, 
      refreshInterval: null, 
      refreshRate: 5000, 
      isFollowingCar: true, 
      tripId: null,
      lastAnimatedPos: null,
      framesPerTick: 60,
      animationFrameId: null,
      lastProcessedRouteLength: 0, // Track how many route points we've already added
      pinnedTrackedPointInfo: false, // Track if info window is pinned by click

      // Trip start
      isStartingTrip: false,
      nowTick: null,       // Date object updated every 30 s for reactivity
      nowInterval: null,
      isTripExpired: false,

      // OTP verification
      otpRequired: false,
      otpInput: '',
      otpError: '',
      otpLoading: false,
      otpSessionToken: null,
    };
  },
  computed: {
    google: gmapApi,

    /** True when current time is within 30 min before departure (or after it) and trip is not expired */
    canStartTrip() {
      if (!this.trip || !this.trip.departure_time) return false;
      const now = this.nowTick || new Date();
      const departure = new Date(this.trip.departure_time);
      const diffMin = (departure - now) / 60000; // positive = future
      return diffMin <= 30; // within window or already past
    },


    /** Formatted time when the start button becomes available */
    departureWindowStart() {
      if (!this.trip || !this.trip.departure_time) return '';
      const t = new Date(new Date(this.trip.departure_time).getTime() - 30 * 60 * 1000);
      return t.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },

  },
  async mounted() {
    // Set locale from store (falls back to 'en' if not set)
    const locale = this.$store.getters.getAppLocale || 'en';
    this.$i18n.locale = locale;
    
    // Set page title
    document.title = this.$t('shared_trip.page_title');

    this.isLoading = false;
    this.token = this.$route.params.token || this.$route.query.token;
    if (!this.token) {
      this.error = this.$t('shared_trip.error_invalid_link');
      return;
    }

    // Always show OTP screen first — trip data is loaded only after verification
    this.otpRequired = true;


    // Tick every 30 s so canStartTrip stays reactive near the window boundary
    this.nowTick = new Date();
    this.nowInterval = setInterval(() => { this.nowTick = new Date(); }, 30000);
  },

  beforeDestroy() {
    // Stop polling when component is destroyed
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    if (this.nowInterval) {
      clearInterval(this.nowInterval);
    }
  },

  methods: {
    async loadSharedTrip() {
      this.isTripExpired = false;

      try {
        const headers = {};
        if (this.otpSessionToken) headers['X-OTP-Session'] = this.otpSessionToken;
        const { data } = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/public/asset-tracker/trip/${this.token}`, { headers });
   
        if (data.otp_required) {
          this.otpRequired = true;
          this.isLoading = false;
          return;
        }

        if(data.success === false) {
          this.error = this.$t('shared_trip.error_link_expired');
          this.isTripExpired = true;
          this.isLoading = false;
          return;
        }

        this.trip = data.trip;
        this.alarm = data.alarm;
        this.gps = data.gps;

        this.startLocation = this.trip.start_position;
        this.endLocation = this.trip.end_position;
        this.definedRoute = this.trip.defined_route || [];
        this.trackedRoute = this.trip.tracked_route || [];
        this.destinations = this.trip.waypoints || [];

        let initialPos = this.startLocation || this.trackedRoute[this.trackedRoute.length - 1] || this.gps?.gps_pos;

        this.carPosition = initialPos;
        this.lastAnimatedPos = initialPos;
        this.lastProcessedRouteLength = this.trackedRoute.length; // Initialize tracking
       

        if (this.trip.trip_log?.length) {
          this.waitingCircles = this.trip.trip_log
            .filter(l => l.type === "waiting_start")
            .map(l => l.position);
        }

        this.isLoading = false;

        if(this.trip.trip_state?.tripEnded && this.trackedRoute.length > 0) {
          clearInterval(this.refreshInterval);
          this.carPosition = this.endLocation;
          this.lastAnimatedPos = this.endLocation;
        }

        this.$nextTick(() => {
          setTimeout(() => {
            this.setWayPoints();
            this.setMapBounds();
          }, 1000);
        });
      } catch (e) {
        this.error = this.$t('shared_trip.error_failed_load');
        this.isStartingTrip = true;
        this.isLoading = false;
      }
    },
    async verifyOtp() {
      this.otpError = '';
      if (!this.otpInput.trim()) return;

      this.otpLoading = true;
      try {
        const { data } = await this.axios.post(
          `${process.env.VUE_APP_SERVER_URL}/public/asset-tracker/trip/${this.token}/verify-otp`,
          { otp: this.otpInput.trim() }
        );

        if (data.success && data.otp_session_token) {
          this.otpSessionToken = data.otp_session_token;
          this.otpRequired = false;
          
          await this.loadSharedTrip();
          this.deviceSelected();
          this.startRealTimeTracking();
        } else if (data.code === 'LINK_EXPIRED') {
          this.error = this.$t('shared_trip.err_link_expired');
        } else {
          this.otpError = this.$t('shared_trip.otp_invalid');
        }
      } catch (err) {
        const code = err.response?.data?.code;
        if (code === 'OTP_INVALID') {
          this.otpError = this.$t('shared_trip.otp_invalid');
        } else if (code === 'OTP_EXPIRED') {
          this.otpError = this.$t('shared_trip.otp_expired');
        } else if (code === 'LINK_EXPIRED') {
          this.error = this.$t('shared_trip.err_link_expired');
        } else {
          this.otpError = this.$t('shared_trip.otp_error');
        }
      } finally {
        this.otpLoading = false;
      }
    },

    showInfoWindow(position, infoText, show) {
      this.infoWindowPos = position;
      this.infoOptions.content = infoText;
      this.infoWinOpen = show;
    },

    setWayPoints() {
      let vm = this;
      this.$nextTick(() => {
        // Filter out breaks or invalid destinations
       const wayStops = vm.destinations
          .filter(d => d.type === 'destination' && d.location)
          .slice(1, -1)
    
        const cleanWayStops =JSON.parse(JSON.stringify(wayStops));
        cleanWayStops.forEach((item) => {
          delete item.address;
          delete item.type;
        });
        vm.waypoints = cleanWayStops;

      });
    },
  
    setMapBounds() {
      var vm = this;

      setTimeout(() => {
        vm.$refs.map.$mapPromise.then((map) => {
          const bounds = new vm.google.maps.LatLngBounds();

          if (vm.startLocation) bounds.extend(vm.startLocation);
          if (vm.endLocation) bounds.extend(vm.endLocation);

          map.fitBounds(bounds);
        });
      }, 500);
    },
    
    formatDate(date) {
      return moment(date).tz('Europe/Stockholm').format('YYYY-MM-DD, HH:mm');
    },

    formatDuration(seconds) {
      const duration = moment.duration(seconds, 'seconds');
      const hours = Math.floor(duration.asHours());
      const minutes = duration.minutes();
      
      if (hours > 0 && minutes > 0) {
        return `${hours}h ${minutes}m`;
      } else if (hours > 0) {
        return `${hours}h`;
      } else if (minutes > 0) {
        return `${minutes}m`;
      } else {
        return `${Math.floor(duration.asSeconds())}s`;
      }
    },

    deviceSelected() {
      if (this.gps && this.gps.imei_number === this.alarm?.imei_number) {
        const gps = this.gps;
        // this.mapCenter = gps.gps_pos;
        let icon = require("@/assets/icon/car.png");
        this.infoOptions.icon = icon;

        let markerObj = {
          // icon: icon,
          position: { lat: gps.gps_pos.lat ? gps.gps_pos.lat : 0, lng: gps.gps_pos.lng ? gps.gps_pos.lng : 0 },
          infoText: this.$t("map_info_popup.alarm_name") + `: <b>${this.alarm.type}</b><br>`,
          alarmId: this.alarm?._id,
          imei: this.alarm?.imei_number,
        };

        //  `${this.$t("map_info_popup.alarm_name")}: <b>${alarm.unit_name}</b><br>
        //           ${this.$t("map_info_popup.gsm_signal_strength")}: <b>${this.getGSMSignalPercent(gps.gsm_signal, alarm.type)}</b><br>
        //           ${this.$t("map_info_popup.battery_percentage")}: <b>${gps.battery_percent} %</b><br>
        //           ${this.$t("map_info_popup.speed")}: <b>${gps.alarm_speed} km/hr</b><br>
        //           Lat: <b>${gps.gps_pos.lat ? gps.gps_pos.lat.toFixed(3) : "-"}</b>, Lng: <b>${gps.gps_pos.lng ? gps.gps_pos.lng.toFixed(3) : "-"}</b>
        //           `,

        setTimeout(() => {
          this.showInfoWindow(gps.gps_pos, markerObj.infoText, true);
        }, 300);
      }
    },

    // Real-time tracking methods
    startRealTimeTracking() {
      if (this.refreshInterval) clearInterval(this.refreshInterval);
      
      this.refreshInterval = setInterval(() => {
        this.updateRealTimeCarPosition();
      }, this.refreshRate);
    },

    async updateRealTimeCarPosition() {
      try {
        const rtHeaders = { "X-Public-Access": "true" };
        if (this.otpSessionToken) rtHeaders['X-OTP-Session'] = this.otpSessionToken;
        const { data } = await this.axios.get(
          `${process.env.VUE_APP_SERVER_URL}/public/asset-tracker/trip/${this.token}`,
          { headers: rtHeaders }
        );

        const trip = data.trip;
        const gps = data.gps;

        if (trip.trip_log && trip.trip_log.length > 0) {
          let waitingPositions = _.filter(trip.trip_log, { type: "waiting_start" });
          this.waitingCircles = waitingPositions.map(w => w.position);
        }
        this.trip.trip_state = trip.trip_state;
        

        let nextPoint = trip.tracked_route?.at(-1) || gps?.gps_pos || null;

        if (!nextPoint || !this.lastAnimatedPos) return;

        const dist = this.calculateDistance(
          this.lastAnimatedPos,
          nextPoint
        );

        if (dist > 0.00005) {
          // Only animate if there are new route points from the API
          if (trip.tracked_route && trip.tracked_route.length > this.lastProcessedRouteLength) {
            this.animateCarAndRoute(this.lastAnimatedPos, nextPoint);
            this.lastProcessedRouteLength = trip.tracked_route.length; // Update tracker
          }
        } 

        if (trip.trip_state?.tripEnded) {
          clearInterval(this.refreshInterval);
          // Ensure car is at final position when trip ends
          
          this.carPosition = this.endLocation;
          this.lastAnimatedPos = this.endLocation;
        }
      } catch (err) {
        console.error(err);
      }
    },
    calculateDistance(pos1, pos2) {
      if (!pos1 || !pos2) return 0;
      const R = 6371; // Earth's radius in km
      const dLat = (pos2.lat - pos1.lat) * Math.PI / 180;
      const dLng = (pos2.lng - pos1.lng) * Math.PI / 180;
      const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(pos1.lat * Math.PI / 180) * Math.cos(pos2.lat * Math.PI / 180) *
        Math.sin(dLng/2) * Math.sin(dLng/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      return R * c;
    },
    

    animateCarMovement(fromPos, toPos, duration) {
      if (!fromPos || !toPos) return;
      
      // Use fixed number of steps spread across the refresh interval
      const steps = 60; // Smooth 60 frames over refresh interval
      const latDiff = toPos.lat - fromPos.lat;
      const lngDiff = toPos.lng - fromPos.lng;
      const stepDuration = duration / steps;

      for (let i = 1; i <= steps; i++) {
        setTimeout(() => {
          const progress = i / steps;
          // Use easing for more natural movement
          const easeProgress = progress < 0.5 
            ? 2 * progress * progress 
            : -1 + (4 - 2 * progress) * progress;
          
          const currentLat = fromPos.lat + (latDiff * easeProgress);
          const currentLng = fromPos.lng + (lngDiff * easeProgress);
          
          this.carPosition = {
            lat: currentLat,
            lng: currentLng
          };
        }, stepDuration * i);
      }
    },

    showCarInfo() {
      const infoText = `
        <b>${this.alarm?.unit_name || this.$t('shared_trip.vehicle')}</b><br>
        ${this.$t('shared_trip.speed')}: <b>${this.gps?.alarm_speed || '-'} km/hr</b><br>
        ${this.$t('shared_trip.position')}: <b>${this.carPosition?.lat.toFixed(4)}, ${this.carPosition?.lng.toFixed(4)}</b>
      `;
      this.showInfoWindow(this.carPosition, infoText, true);
    },

    showTrackedPointInfo(point, index) {
      const time  = point?.timestamp ? moment(point.timestamp).tz('Europe/Stockholm').format('HH:mm:ss') : '—';
      const speed = point?.speed != null ? point.speed.toFixed(1) : '—';
      const lat   = point?.lat  != null ? point.lat.toFixed(6)  : '—';
      const lng   = point?.lng  != null ? point.lng.toFixed(6)  : '—';
      const fuel  = point?.fuelLevel != null ? point.fuelLevel : null;

      const fuelColor = fuel == null ? '#94a3b8' : fuel > 50 ? '#00C16E' : fuel > 20 ? '#F29100' : '#ef4444';
      const fuelBar = fuel != null ? `
        <div style="background:#f8fafc;border-radius:8px;padding:7px 10px;grid-column:span 2;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:5px;">
            <div style="font-size:10px;color:#94a3b8;font-weight:600;letter-spacing:.05em;text-transform:uppercase;">${this.$t('shared_trip.fuel_level')}</div>
            <div style="font-size:13px;font-weight:700;color:${fuelColor};">${fuel}<span style="font-size:10px;font-weight:500;color:#94a3b8;"> %</span></div>
          </div>
          <div style="height:6px;background:#e2e8f0;border-radius:99px;overflow:hidden;">
            <div style="height:100%;width:${Math.min(fuel,100)}%;background:${fuelColor};border-radius:99px;transition:width .3s;"></div>
          </div>
        </div>` : '';

      const infoText = `
        <div style="font-family:'Segoe UI',system-ui,sans-serif;min-width:200px;padding:2px 0;">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
            <div>
              <div style="font-size:11px;font-weight:600;color:#1A8DE9;letter-spacing:.06em;text-transform:uppercase;">${this.$t('shared_trip.route_point')}</div>
              <div style="font-size:15px;font-weight:700;color:#1e293b;line-height:1.2;">#${index}</div>
            </div>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;">
            <div style="background:#f8fafc;border-radius:8px;padding:7px 10px;">
              <div style="font-size:10px;color:#94a3b8;font-weight:600;letter-spacing:.05em;text-transform:uppercase;margin-bottom:2px;">${this.$t('shared_trip.time')}</div>
              <div style="font-size:13px;font-weight:700;color:#1e293b;">${time}</div>
            </div>
            <div style="background:#f8fafc;border-radius:8px;padding:7px 10px;">
              <div style="font-size:10px;color:#94a3b8;font-weight:600;letter-spacing:.05em;text-transform:uppercase;margin-bottom:2px;">${this.$t('shared_trip.speed')}</div>
              <div style="font-size:13px;font-weight:700;color:#F29100;">${speed} <span style="font-size:10px;font-weight:500;color:#94a3b8;">km/h</span></div>
            </div>
            <div style="background:#f8fafc;border-radius:8px;padding:7px 10px;">
              <div style="font-size:10px;color:#94a3b8;font-weight:600;letter-spacing:.05em;text-transform:uppercase;margin-bottom:2px;">${this.$t('shared_trip.lat')}</div>
              <div style="font-size:12px;font-weight:600;color:#475569;font-variant-numeric:tabular-nums;">${lat}</div>
            </div>
            <div style="background:#f8fafc;border-radius:8px;padding:7px 10px;">
              <div style="font-size:10px;color:#94a3b8;font-weight:600;letter-spacing:.05em;text-transform:uppercase;margin-bottom:2px;">${this.$t('shared_trip.lng')}</div>
              <div style="font-size:12px;font-weight:600;color:#475569;font-variant-numeric:tabular-nums;">${lng}</div>
            </div>
            ${fuelBar}
          </div>
        </div>
      `;
      this.showInfoWindow(point, infoText, true);
    },

    closeTrackedPointInfo() {
      this.infoWinOpen = false;
      this.pinnedTrackedPointInfo = false;
    },

    closeTrackedPointInfoUnlessPinned() {
      if (!this.pinnedTrackedPointInfo) {
        this.infoWinOpen = false;
      }
    },

    pinTrackedPointInfo(point, index) {
      this.showTrackedPointInfo(point, index);
      this.pinnedTrackedPointInfo = true;
    },

    animateCarAndRoute(from, to) {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
      }

      const frames = this.framesPerTick;
      let frame = 0;

      const latDiff = to.lat - from.lat;
      const lngDiff = to.lng - from.lng;

      const animate = () => {
        frame++;
        const t = frame / frames;

        const eased =
          t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

        const current = {
          lat: from.lat + latDiff * eased,
          lng: from.lng + lngDiff * eased,
        };

        this.carPosition = current;
        
        // Only add significant points to avoid too many intermediate points
        if (frame % 6 === 0 || frame === frames) {
          this.trackedRoute.push(current);
        }

        if (this.isFollowingCar) {
          this.mapCenter = current;
        }

        if (frame < frames) {
          this.animationFrameId = requestAnimationFrame(animate);
        } else {
          this.lastAnimatedPos = to;
        }
      };

      animate();
    },
    generateMapLink(mapType) {
      if (!this.trip) return '#';
      let destinations = this.destinations?.filter(d => d.type === 'destination' && d.location) || [];
      const startWaypoint = destinations.length > 0 ? destinations[0] : null;

      const endWaypoint = destinations.length > 1 ? destinations[destinations.length - 1] : null;

      const origin = startWaypoint
        ? (startWaypoint.address ? encodeURIComponent(startWaypoint.address) : `${startWaypoint.location.lat},${startWaypoint.location.lng}`)
        : (this.trip.start_position ? `${this.trip.start_position.lat},${this.trip.start_position.lng}` : 'My+Location');
      const destination = endWaypoint
        ? (endWaypoint.address ? encodeURIComponent(endWaypoint.address) : `${endWaypoint.location.lat},${endWaypoint.location.lng}`)
        : (this.trip.end_position ? `${this.trip.end_position.lat},${this.trip.end_position.lng}` : '');

      const waypointsArray = destinations && destinations.length > 2
        ? destinations.slice(1, -1).map(w => `${w.location.lat},${w.location.lng}`)
        : [];
        
      const waypoints = waypointsArray.join('|');

      if (mapType === 'google') {
        let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
        if (waypoints) url += `&waypoints=${waypoints}`;

        return url;
      }
      if (mapType === 'apple') {
        // Apple Maps: always use lat,lng coordinates — address strings contain "Name | Address"
        
        const appleSource = startWaypoint
          ? `${startWaypoint.location.lat},${startWaypoint.location.lng}`
          : (this.trip.start_position ? `${this.trip.start_position.lat},${this.trip.start_position.lng}` : null);

        const appleDest = endWaypoint
          ? `${endWaypoint.location.lat},${endWaypoint.location.lng}`
          : (this.trip.end_position ? `${this.trip.end_position.lat},${this.trip.end_position.lng}` : null);

        if (!appleSource || !appleDest) return '#';

        let appleUrl = `https://maps.apple.com/directions?source=${appleSource}&destination=${appleDest}&mode=driving`;

        // Add each intermediate stop as a separate &waypoint= param using coordinates
        if (destinations.length > 2) {
          destinations.slice(1, -1).forEach(w => {
            appleUrl += `&waypoint=${w.location.lat},${w.location.lng}`;
          });
        }

        return appleUrl;
      }
      return '#';
    },
    openMapLink(mapType) {
      const url = this.generateMapLink(mapType);
      
      if (url !== '#') {
        window.open(url, '_blank');
      }
    },
    checkIOS() {
      const userAgent = window.navigator.userAgent || window.navigator.vendor || window.opera;
      return /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    },

    async startTrip() {
      if (!this.canStartTrip || this.isStartingTrip) return;
      this.isStartingTrip = true;
      try {
        const { data } = await this.axios.post(
          `${process.env.VUE_APP_SERVER_URL}/public/asset-tracker/trip/${this.token}/start`
        );
        if (data.success) {
          this.handleSuccess(this.$t('shared_trip.trip_started_success'));
          this.trip.trip_state.tripStarted = true;
          this.startRealTimeTracking();
        } else {
          this.handleError(null, this._tripStartErrorMsg(data));
        }
      } catch (e) {
        const errData = e.response?.data;
        this.handleError(e, this._tripStartErrorMsg(errData));
      } finally {
        this.isStartingTrip = false;
      }
    },

    /** Map a backend error response to a translated message */
    _tripStartErrorMsg(data) {
      if (!data) return this.$t('shared_trip.trip_start_error');
      const codeMap = {
        INVALID_TOKEN:   'shared_trip.err_invalid_token',
        LINK_EXPIRED:    'shared_trip.err_link_expired',
        NOT_FOUND:       'shared_trip.err_not_found',
        ALREADY_STARTED: 'shared_trip.err_already_started',
        TOO_EARLY: () => {
          const time = data.availableFrom
            ? new Date(data.availableFrom).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : this.departureWindowStart;
          return this.$t('shared_trip.err_too_early', { time });
        },
      };
      const entry = codeMap[data.code];
      if (!entry) return this.$t('shared_trip.trip_start_failed');
      return typeof entry === 'function' ? entry() : this.$t(entry);
    },

  }
};
</script>

<style scoped>
.shared-trip-container {
  width: 100%;
  height: calc(100vh - 130px);
  overflow: hidden;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 130px);
  padding: 20px;
}

.trip-view {
  display: flex;
  height: calc(100vh - 130px);
}

.map-section {
  flex: 1;
  position: relative;
}

.map-container {
  width: 100%;
  height: 100%;
}

.details-section {
  width: 450px;
  background: white;
  overflow-y: auto;
  border-left: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
}

.details-header {
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(to bottom, #f9fafb, #ffffff);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.details-content {
  flex: 1;
  padding: 20px;
}

.info-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
}

.info-card-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e5e7eb;
}

.info-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-item {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0px 8px 5px 8px;
  border-bottom: 1px solid #f3f4f6;
}

.info-item:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  flex-shrink: 0;
  min-width: 140px;
}

.info-value {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  text-align: right;
  flex: 1;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.status-not-started {
  background: #f3f4f6;
  color: #6b7280;
}

.status-ongoing {
  background: #fef3c7;
  color: #92400e;
}

.status-completed {
  background: #d1fae5;
  color: #065f46;
}

.text-xxs {
  font-size: 0.65rem;
  line-height: 1rem;
  letter-spacing: 0.025em;
}

.branding {
  text-align: center;
  padding: 20px 0;
  margin-top: 20px;
  border-top: 1px solid #e5e7eb;
}

/* ── Trip Start Button ───────────────────────────────────────── */
.trip-start-wrap {
  margin-bottom: 20px;
}

.trip-start-note {
  margin-top: 6px;
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
  min-height: 16px;
}

.start-trip-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 11px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.02em;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  gap: 6px;
}

.start-trip-btn-active {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  color: #fff;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.35);
}

.start-trip-btn-active:hover {
  background: linear-gradient(135deg, #1d4ed8, #1e40af);
  box-shadow: 0 6px 16px rgba(37, 99, 235, 0.45);
  transform: translateY(-1px);
}

.start-trip-btn-disabled {
  background: #e2e8f0;
  color: #94a3b8;
  cursor: not-allowed;
}

.btn-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Mid-size tablets: narrow sidebar */
@media (max-width: 1024px) and (min-width: 769px) {
  .details-section {
    width: 360px;
  }

  .details-header {
    padding: 18px 20px;
  }

  .details-content {
    padding: 16px;
  }
}

/* Tablet and mobile: stacked layout */
@media (max-width: 768px) {
  .shared-trip-container {
    height: auto;
    min-height: calc(100vh - 130px);
    overflow: visible;
  }

  .loading-container,
  .error-container {
    height: auto;
    min-height: calc(100vh - 130px);
  }

  .trip-view {
    flex-direction: column;
    height: auto;
    min-height: calc(100vh - 130px);
  }

  .map-section {
    height: 50vh;
    min-height: 260px;
    flex-shrink: 0;
  }

  .map-container {
    height: 50vh;
    min-height: 260px;
  }

  .details-section {
    width: 100%;
    height: auto;
    border-left: none;
    border-top: 1px solid #e5e7eb;
    overflow-y: visible;
    flex-shrink: 0;
  }

  .details-header {
    padding: 14px 16px;
    position: static;
  }

  .details-content {
    padding: 14px;
  }

  .info-card {
    padding: 14px;
    margin-bottom: 14px;
  }
}

/* Small phones */
@media (max-width: 480px) {
  .map-section {
    height: 45vh;
    min-height: 220px;
  }

  .map-container {
    height: 45vh;
    min-height: 220px;
  }

  .details-header {
    padding: 12px 14px;
  }

  .details-content {
    padding: 10px;
  }

  .info-card {
    padding: 12px;
    margin-bottom: 10px;
    border-radius: 8px;
  }

  .info-card-title {
    font-size: 14px;
    margin-bottom: 10px;
    padding-bottom: 8px;
  }

  .info-label {
    min-width: 100px;
    font-size: 12px;
  }

  .info-value {
    font-size: 12px;
  }

  .status-badge {
    padding: 3px 8px;
    font-size: 11px;
  }

  .info-item {
    padding: 0px 4px 5px 4px;
  }
}

/* 1. Remove the large header space and move close button to the top-right corner */
::v-deep .gm-style-iw-chr {
  height: 0px !important;
  position: absolute !important;
  top: 0 !important;
  right: 0 !important;
  z-index: 1;
}

/* 2. Style the default button to be smaller and cleaner */
::v-deep .gm-style-iw-chr button {
  top: -8px !important;
  right: -5px !important;
  width: 45px !important;
  height: 28px !important;
}
::v-deep .gm-style-iw-chr button span {
  width: 18px !important;
  height: 18px !important;
  margin: 15px !important;
}

/* 3. Remove all padding from the main window container for a flush look */
::v-deep .gm-style-iw-c {
  padding: 0 !important;
}

/* 4. Remove internal margins so your content starts at the very top */
::v-deep .gm-style-iw-d {
  margin: 0 !important;
  padding: 5px !important;
  overflow: hidden !important;
}

/* OTP Verification Screen */
.otp-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 130px);
  padding: 20px;
  background: #f9fafb;
}

.otp-card {
  background: white;
  border-radius: 16px;
  padding: 40px 48px;
  max-width: 420px;
  width: 100%;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.10);
  border: 1px solid #e5e7eb;
}

.otp-icon-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 64px;
  height: 64px;
  background: #EBF5FD;
  border-radius: 50%;
  margin: 0 auto 20px;
  border: 2px solid #D4EAFB;
}

.otp-lock-icon {
  font-size: 28px;
  color: #1A8DE9;
}

.otp-title {
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 10px;
}

.otp-desc {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 24px;
  line-height: 1.6;
}

.otp-input-group {
  margin-bottom: 4px;
  display: flex;
  flex-direction: column;
}

.otp-error-container {
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.otp-input {
  width: 100%;
  padding: 14px 16px;
  font-size: 21px;
  font-weight: 700;
  letter-spacing: 10px;
  text-align: center;
  border: 2px solid #d1d5db;
  border-radius: 10px;
  outline: none;
  color: #111827;
  transition: border-color 0.2s;
  font-family: monospace;
}

.otp-input:focus {
  border-color: #1A8DE9;
  box-shadow: 0 0 0 3px rgba(26, 141, 233, 0.15);
}

.otp-input-error {
  border-color: #ef4444 !important;
}

.otp-error-msg {
  margin: 0;
  font-size: 13px;
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
}

.otp-verify-btn {
  width: 100%;
  height: 48px; 
  padding: 13px 20px;
  background: #1A8DE9;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 4px 12px rgba(26, 141, 233, 0.3);
  margin-bottom: 16px;
}

.otp-verify-btn:hover:not(:disabled) {
  background: #1272BE;
  box-shadow: 0 6px 16px rgba(26, 141, 233, 0.4);
}

.otp-verify-btn:active:not(:disabled) {
  background: #1C497C;
}

.otp-verify-btn:disabled {
  background: #e2e8f0;
  color: #94a3b8;
  cursor: not-allowed;
  box-shadow: none;
}

/* Ensure the button stays branding color during the "verifying" spinner state */
.otp-verify-btn:disabled:has(.fa-spin) {
  background: #1A8DE9;
  color: rgba(255, 255, 255, 0.8);
  cursor: wait;
  box-shadow: 0 4px 12px rgba(26, 141, 233, 0.2);
}

.otp-hint {
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
}
</style>
